import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight, Sparkles } from "lucide-react";
import api from "@/api/client";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("order_id");
  const [state, setState] = useState({ status: "checking", data: null });

  useEffect(() => {
    if (!orderId) {
      setState({ status: "error", data: "Missing order id" });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/razorpay/order/${orderId}/status`);
        if (data.payment?.status === "captured" && data.subscription) {
          setState({ status: "paid", data });
          setTimeout(() => navigate("/app/dashboard"), 4000);
        } else if (data.payment?.status === "failed") {
          setState({ status: "failed", data });
        } else {
          setState({ status: "pending", data });
        }
      } catch (e) {
        setState({ status: "error", data: e.message });
      }
    })();
  }, [orderId, navigate]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
      <div className="max-w-md w-full border border-slate-800 rounded-2xl p-10 text-center bg-gradient-to-b from-slate-900/60 to-transparent">
        {state.status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-sky-400 animate-spin mx-auto mb-4" />
            <h2 className="font-serif text-2xl text-sky-100">Confirming your payment…</h2>
            <p className="text-sm text-slate-500 mt-2">This takes just a few seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400">payment captured</p>
            <h2 className="font-serif text-3xl text-sky-100 mt-2">You're subscribed 🎉</h2>
            <p className="text-sm text-slate-400 mt-3">
              <span className="font-medium text-sky-200 capitalize">{state.data.subscription?.plan_slug}</span> plan is now active until{" "}
              <span className="text-sky-300 font-mono">
                {new Date(state.data.subscription?.ends_at).toLocaleDateString()}
              </span>
            </p>
            <p className="text-[11px] font-mono text-slate-500 mt-2">
              ₹{(state.data.subscription?.amount / 100).toLocaleString("en-IN")} · Payment #{state.data.payment?.razorpay_payment_id}
            </p>
            <Link
              to="/app/dashboard"
              data-testid="btn-payment-success-continue"
              className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium"
            >
              <Sparkles className="w-4 h-4" /> Open Dashboard <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="text-[10px] text-slate-600 mt-3">Auto-redirecting in 4 seconds…</p>
          </>
        )}
        {state.status === "pending" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-4" />
            <h2 className="font-serif text-2xl text-sky-100">Payment is processing</h2>
            <p className="text-sm text-slate-400 mt-3">
              Your bank is still confirming this payment. You'll receive access shortly — this can take up to a few minutes for some banks.
            </p>
            <p className="text-[11px] font-mono text-slate-500 mt-3">Order: {orderId}</p>
            <Link
              to="/app/dashboard"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 border border-sky-400 text-sky-200 hover:bg-sky-500/10 rounded"
            >
              Go to Dashboard <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        {state.status === "failed" && (
          <>
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="font-serif text-2xl text-sky-100">Payment could not be captured</h2>
            <p className="text-sm text-slate-400 mt-3">Please try again with a different payment method.</p>
            <Link
              to="/pricing"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded font-medium"
            >
              Back to Pricing
            </Link>
          </>
        )}
        {state.status === "error" && (
          <>
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="font-serif text-2xl text-sky-100">Could not verify payment</h2>
            <p className="text-sm text-slate-400 mt-3">{String(state.data)}</p>
            <Link
              to="/pricing"
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 border border-sky-400 text-sky-200 hover:bg-sky-500/10 rounded"
            >
              Back to Pricing
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
